import { Dialog, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import { useLoggedInUser } from 'app/auth/useLoggedInUser';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormAutocompleteField } from 'framework/forms/FormAutocompleteField';
import { FormSelectFieldFromStrings } from 'framework/forms/FormSelectFieldFromStrings';
import { FormTextField } from 'framework/forms/FormTextField';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { ILocationSummary, IRegisterUserRequest, IUserInfo, UserRole, usersCommand_register } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useContext, useEffect, useMemo } from 'react';
import { RecordContext } from 'shared/records/RecordContext';
import * as yup from 'yup';
import { useNonDeletedLocationSummaries } from '../../locations/useNonDeletedLocationSummaries';
import { userToSelectableRoles } from '../utils/userToSelectableRoles';

const createSchema = (strings: IStrings) => {
	return yup.object<IRegisterUserRequest>({
		salutation: yup.string(),
		firstName: yup.string().required(strings.formRequired(strings.firstName)),
		lastName: yup.string().required(strings.formRequired(strings.lastName)),
		email: yup.string().email().required(strings.formRequired(strings.email)),
		preferredLocationId: yup.string().required(),
		role: yup.mixed<UserRole>().required(),
	});
};

interface IProps extends DialogProps {
	confirm: VoidFunction;
	cancel: VoidFunction;
}

export const RegisterForm = ({ confirm, cancel, ...rest }: IProps) => {
	const notify = useSnackbarNotify();
	const [register, isSubmitting] = useFormSubmit(usersCommand_register);
	const strings = useLocalization();
	const [locations] = useNonDeletedLocationSummaries();
	const user = useLoggedInUser();

	const handleSubmit = async (values: IRegisterUserRequest, helpers: FormikHelpers<IRegisterUserRequest>) => {
		const r = await register(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.userRegisteredCheckEmail(values.email));
			confirm();
		}
	};

	if (locations === undefined) {
		return <div></div>;
	}

	return (
		<Formik<IRegisterUserRequest>
			validateOnMount
			initialValues={{
				salutation: '',
				firstName: '',
				lastName: '',
				email: '',
				preferredLocationId: '',
				role: 'Normal' as UserRole,
			}}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<InnerDialog
					{...rest}
					isSubmitting={isSubmitting}
					locations={locations}
					cancel={cancel}
					user={user}
				/>
			</Form>
		</Formik>
	);
};

interface IInnerDialogProps extends DialogProps {
	cancel: VoidFunction;
	isSubmitting: boolean;
	locations: ILocationSummary[];
	user: IUserInfo;
}

const InnerDialog = ({ cancel, isSubmitting, locations, user, ...rest }: IInnerDialogProps) => {
	const props = useFormikContext<IRegisterUserRequest>();
	const strings = useLocalization();
	const userRoleOptions = useMemo(() => userToSelectableRoles(user), [user]);
	const { userRoleRecord } = useContext(RecordContext);

	useEffect(() => {
		if (locations.length === 1) {
			setFormValue<IRegisterUserRequest>('preferredLocationId', locations[0].id, props);
		}
		// eslint-disable-next-line
	}, [locations]);

	return (
		<Dialog
			{...rest}
			fullWidth
			maxWidth='xs'>
			<DialogTitle>{strings.registerNewUser}</DialogTitle>
			<DialogContent
				className='df-col'
				dividers>
				<FormTextField<IRegisterUserRequest>
					name='email'
					label={strings.email}
					required
				/>
				<FormTextField<IRegisterUserRequest>
					name='salutation'
					label={strings.salutation}
				/>
				<FormTextField<IRegisterUserRequest>
					name='firstName'
					label={strings.firstName}
					required
				/>
				<FormTextField<IRegisterUserRequest>
					name='lastName'
					label={strings.lastName}
					required
				/>
				<FormAutocompleteField<IRegisterUserRequest, ILocationSummary>
					name='preferredLocationId'
					label={strings.location}
					options={locations}
				/>
				<FormSelectFieldFromStrings<IRegisterUserRequest>
					name='role'
					options={userRoleOptions}
					label={strings.role}
					renderValue={t => userRoleRecord[t as UserRole]}
				/>
			</DialogContent>
			<CancelSubmitFormDialogActions
				submitText={strings.create}
				isSubmitting={isSubmitting}
				cancel={cancel}
			/>
		</Dialog>
	);
};
